import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, Users } from "lucide-react";
import api from "../../lib/api";
import { EmptyState, KpiCard, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function Recursos() {
  const [openAlert, setOpenAlert] = useState(null);
  const { data } = useQuery({
    queryKey: ["recursos-global"],
    queryFn: async () => (await api.get("/proyectos/recursos/global")).data,
  });
  const { data: projects } = useQuery({ queryKey: ["proyectos"], queryFn: async () => (await api.get("/proyectos")).data });
  if (!data) return null;
  const projName = (id) => projects?.find((p) => p.id === id)?.name || id;
  const resById = Object.fromEntries(data.resources.map((r) => [r.id, r]));
  const over = data.alerts.filter((a) => a.type === "sobreasignacion");

  return (
    <div data-testid="recursos-page">
      <PageHeader title="Recursos del portafolio" subtitle="Capacidad, asignaciones y conflictos de sobreasignación entre proyectos. Cada alerta abre exactamente las asignaciones que la generan." />

      <div className="grid gap-3 sm:grid-cols-3">
        <KpiCard testid="kpi-resources" title="Recursos" value={data.resources.length} icon={Users} tone="info" subtitle={`${data.resources.filter((r) => r.kind === "persona").length} personas · ${data.resources.filter((r) => r.kind === "empresa").length} equipos/empresas`} />
        <KpiCard testid="kpi-allocations" title="Asignaciones activas" value={data.allocations.length} icon={Users} tone="muted" />
        <KpiCard testid="kpi-overallocated" title="Sobreasignaciones" value={over.length} icon={AlertOctagon} tone={over.length ? "danger" : "success"} subtitle=">100% de capacidad en el mismo periodo" />
      </div>

      <h2 className="ts-section-title mb-3 mt-6">Conflictos de sobreasignación</h2>
      {over.length === 0 && <EmptyState title="Sin sobreasignaciones" description="Ningún recurso supera el 100% de capacidad en un mismo periodo." testid="no-overallocations" />}
      <div className="space-y-2" data-testid="overallocation-list">
        {over.map((al, i) => {
          const conflict = al.allocations || data.allocations.filter((a) => a.resource_id === al.resource_id && a.period === al.period);
          return (
            <div key={`${al.resource_id}-${al.period}`} className="ts-card p-4" data-testid={`over-card-${al.resource_id}-${al.period}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{al.resource_name} <span className="font-mono text-red-600">{al.total_pct}%</span> en {al.period}</p>
                  <p className="text-xs text-slate-500">{al.projects.length > 1 ? `Conflicto entre ${al.projects.length} proyectos: ${al.projects.map(projName).join(" · ")}` : `En ${projName(al.projects[0])}`}</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpenAlert(openAlert === i ? null : i)} data-testid={`over-drill-${i}`}>
                  {openAlert === i ? "Ocultar" : "Ver asignaciones que chocan"}
                </Button>
              </div>
              {openAlert === i && (
                <div className="mt-3 overflow-x-auto rounded-sm border border-slate-100" data-testid={`over-detail-${i}`}>
                  <Table>
                    <TableHeader><TableRow><TableHead>Proyecto</TableHead><TableHead>Actividad</TableHead><TableHead>Periodo</TableHead><TableHead className="text-right">Asignación</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {conflict.map((a) => (
                        <TableRow key={a.id} data-testid={`over-row-${a.id}`}>
                          <TableCell className="text-xs">{a.project_name || projName(a.project_id)}</TableCell>
                          <TableCell className="text-xs">{a.activity_name || (a.activity_id ? a.activity_id.slice(0, 22) : "asignación general")}</TableCell>
                          <TableCell className="font-mono text-xs">{a.period}</TableCell>
                          <TableCell className="text-right font-mono text-xs font-bold text-red-600">{a.allocation_pct}%</TableCell>
                          <TableCell><Link to={`/proyectos/planificacion?project=${a.project_id}&tab=recursos`} className="text-xs text-blue-700 hover:underline" data-testid={`over-link-${a.id}`}>Ir al proyecto →</Link></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <h2 className="ts-section-title mb-3 mt-6">Todos los recursos</h2>
      <div className="ts-card overflow-x-auto" data-testid="resources-table">
        <Table>
          <TableHeader><TableRow><TableHead>Recurso</TableHead><TableHead>Tipo</TableHead><TableHead>Rol</TableHead><TableHead>Empresa</TableHead><TableHead className="text-right">Capacidad</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.resources.map((r) => (
              <TableRow key={r.id} data-testid={`resource-row-${r.id}`}>
                <TableCell className="text-sm font-medium">{r.name}</TableCell>
                <TableCell className="text-xs">{r.kind}</TableCell>
                <TableCell className="text-xs">{r.role}</TableCell>
                <TableCell className="text-xs">{r.company}</TableCell>
                <TableCell className="text-right font-mono text-xs">{r.capacity_pct}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
